import { translate, type Lang } from './i18n'
import type { QualityReport } from './api'
import { fmtDate } from './time'

/**
 * Calidad de datos (§8.2).
 *
 * Un incidente con defectos no se descarta al ingerirlo: se conserva marcado, precisamente para
 * que esta pantalla pueda listarlo y explicarlo. Descartarlos en la ingesta los volvería
 * invisibles, y lo que no se ve no se arregla.
 *
 * Los números de arriba contestan «¿cuánto de lo que tengo sirve?». La tabla de defectos contesta
 * «¿qué arreglo primero?». Y la muestra de abajo trae filas de verdad, porque nadie corrige un
 * porcentaje: se corrige la fila que se ve.
 */
export function DataQuality({ lang, report }: { lang: Lang; report: QualityReport | null }) {
  if (!report) return null

  const cuando = (iso: string | null) =>
    iso ? fmtDate(iso, lang) : '—'

  const porcentaje = (n: number) =>
    report.total === 0 ? '0%' : `${Math.round((n / report.total) * 100)}%`

  return (
    <section className="panel">
      <h2>{translate(lang, 'quality.title')}</h2>
      <p className="notice">{translate(lang, 'quality.help')}</p>

      {report.total === 0 ? (
        <div className="card"><p className="notice">{translate(lang, 'quality.empty')}</p></div>
      ) : (
        <>
          <div className="card">
            <div className="row-line">
              <strong>{translate(lang, 'quality.usable', [report.mappable, porcentaje(report.mappable)])}</strong>
            </div>
            <div className="meta">
              {translate(lang, 'quality.breakdown', [report.ok, report.review, report.unusable])}
            </div>
          </div>

          {report.flags.length > 0 && (
            <div className="card">
              <h3>{translate(lang, 'quality.defects')}</h3>
              <table className="compare">
                <thead>
                  <tr>
                    <th>{translate(lang, 'quality.defect')}</th>
                    <th>{translate(lang, 'quality.count')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {report.flags.map((f) => (
                    <tr key={f.flag}>
                      <td>{translate(lang, `flag.${f.flag}`)}</td>
                      <td className="num">{f.count}</td>
                      <td className="meta">{porcentaje(f.count)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="notice">{translate(lang, 'quality.defectsHelp')}</p>
            </div>
          )}

          <div className="card">
            <h3>{translate(lang, 'quality.sources')}</h3>
            <p className="notice">{translate(lang, 'quality.sourcesHelp')}</p>
            <table className="compare">
              <thead>
                <tr>
                  <th>{translate(lang, 'quality.source')}</th>
                  <th>{translate(lang, 'quality.count')}</th>
                  <th>{translate(lang, 'quality.unusableShort')}</th>
                  <th>{translate(lang, 'quality.from')}</th>
                  <th>{translate(lang, 'quality.until')}</th>
                </tr>
              </thead>
              <tbody>
                {report.sources.map((s) => (
                  <tr key={s.source}>
                    <td>{s.source}</td>
                    <td className="num">{s.count}</td>
                    <td className="num">{s.unusable}</td>
                    <td>{cuando(s.first)}</td>
                    <td>{cuando(s.last)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {report.samples.length > 0 && (
            <div className="card">
              <h3>{translate(lang, 'quality.samples')}</h3>
              <p className="notice">{translate(lang, 'quality.samplesHelp')}</p>
              <div className="rows">
                {report.samples.map((s) => (
                  <div key={s.incidentId} className="row-line">
                    <span className={s.status === 'Unusable' ? 'error' : 'meta'}>
                      {translate(lang, `qualityStatus.${s.status}`)}
                    </span>
                    <span className="grow">
                      {s.externalId} · {s.offenseName}
                      {s.blockAddress ? ` · ${s.blockAddress}` : ''}
                    </span>
                    <span className="meta">
                      {s.flags.map((f) => translate(lang, `flag.${f}`)).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  )
}
